import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { Trash2, ChevronUp, ChevronDown, GripVertical } from 'lucide-react'

type Props = {
    episode: any
    index: number
    total: number
    onDelete: (id: string, type: string) => void
    onMoveUp: () => void
    onMoveDown: () => void
}

export function SortableEpisodeRow({
    episode,
    index,
    total,
    onDelete,
    onMoveUp,
    onMoveDown,
}: Props) {
    const {
        setNodeRef,
        attributes,
        listeners,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: episode.id })

    const getTypeIcon = (type) => {
        const icons = {
            show: '📺',
            episode: '🎬',
            collection: '📚',
        }
        return icons[type] || '📌'
    }

    const getTypeColor = (type) => {
        const colors = {
            show: 'bg-blue-100 text-blue-800 border-blue-300',
            episode: 'bg-purple-100 text-purple-800 border-purple-300',
            collection: 'bg-green-100 text-green-800 border-green-300',
        }
        return colors[type] || 'bg-gray-100 text-gray-800 border-gray-300'
    }

    return (
        <div
            ref={setNodeRef}
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
            }}
            className={`
        flex items-center justify-between
        p-3 border rounded bg-white dark:bg-slate-700 dark:border-gray-600
        ${isDragging ? 'opacity-50' : ''}
      `}
        >
            {/* LEFT */}
            <div className="flex gap-3 items-center">
                {/* DRAG HANDLE (ONLY THIS DRAGS) */}
                <div
                    {...attributes}
                    {...listeners}
                    className="cursor-grab text-gray-400 hover:text-gray-700"
                    title="Drag to reorder"
                >
                    <GripVertical
                        size={18}
                        className="text-gray-400 flex-shrink-0"
                    />
                </div>

                <div className="bg-gray-900 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0">
                    {index + 1}
                </div>
                <img
                    src={episode.thumbnail}
                    className="w-8 h-8 rounded object-cover"
                />

                <div className='flex align-middle justify-between items-center gap-2'>
                    <div>
                        <p className="font-medium dark:text-white ">{episode.name}</p>
                        <p className="text-xs text-gray-500 dark:text-white ">
                            Id: {episode.id}
                        </p>
                    </div>
                    <div>
                        {/* <span className="text-lg">
                            {getTypeIcon(episode.type)}
                        </span> */}
                        <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold border whitespace-nowrap ${getTypeColor(
                                episode.type
                            )}`}
                        >
                            {episode.type}
                        </span>
                    </div>
                </div>
            </div>

            {/* RIGHT ACTIONS */}
            <div className="flex items-center gap-1">
                <button
                    type="button"
                    disabled={index === 0}
                    onClick={onMoveUp}
                    className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
                >
                    <ChevronUp size={16} />
                </button>

                <button
                    type="button"
                    disabled={index === total - 1}
                    onClick={onMoveDown}
                    className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
                >
                    <ChevronDown size={16} />
                </button>

                <button
                    type="button"
                    onClick={() => onDelete(episode.id, episode.type)}
                    className="text-red-500 hover:text-red-700"
                >
                    <Trash2 size={16} />
                </button>
                <div className="relative inline-block group ml-2">
                    <InformationCircleIcon className="w-4 h-4 text-slate-500 cursor-pointer" />

                    <div
                        className="
      absolute top-1/2 right-full mr-3 -translate-y-1/2
      w-64
      rounded-md
      bg-white dark:bg-slate-800
      border border-slate-200 dark:border-slate-700
      p-3 text-xs text-slate-700 dark:text-slate-200
      shadow-xl
      opacity-0 invisible
      group-hover:opacity-100 group-hover:visible
      transition-all duration-150
      z-[9999]
      whitespace-normal
    "
                    >
                        You can change the order of items (up to one level) using the up and down arrows.
                    </div>
                </div>
            </div>
        </div>
    )
}
